import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FiPlus, FiDollarSign, FiSettings, FiExternalLink } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useAllTransactions, useAllPaymentIssues, useAllUsers, createPaymentIssue } from '../../hooks/useAdmin';
import { usePaymentSettings } from '../../hooks/useSettings';
import { callUpdateFeePercent, callAdminResolvePayment } from '../../hooks/useStripe';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { StatusBadge } from '../common/StatusBadge';
import { formatRelativeTime } from '../../utils/formatters';
import { Price } from '../common/Price';

export default function AdminPaymentsIncoming() {
  const { currentUser } = useAuth();
  const { transactions, loading: txLoading } = useAllTransactions();
  const { issues } = useAllPaymentIssues();
  const { users } = useAllUsers();
  const { feePercent, loading: feeLoading } = usePaymentSettings();
  const [statusFilter, setStatusFilter] = useState('all');
  const createModal = useDisclosure();
  const feeModal = useDisclosure();
  const toast = useToast();

  // Fee config state
  const [newFeePercent, setNewFeePercent] = useState('');
  const [updatingFee, setUpdatingFee] = useState(false);
  const [resolving, setResolving] = useState(null);

  // Create issue form state
  const [formUserId, setFormUserId] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formType, setFormType] = useState('card_failed');
  const [formDescription, setFormDescription] = useState('');
  const [formBountyId, setFormBountyId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Build a lookup of payment issues by claimId for error details
  const issuesByClaimId = useMemo(() => {
    const map = {};
    for (const issue of issues) {
      if (issue.claimId) {
        // Keep the most recent issue per claim
        if (!map[issue.claimId] || (issue.createdAt?.seconds > (map[issue.claimId].createdAt?.seconds || 0))) {
          map[issue.claimId] = issue;
        }
      }
    }
    return map;
  }, [issues]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return transactions;
    return transactions.filter((t) => t.paymentStatus === statusFilter);
  }, [transactions, statusFilter]);

  const resetForm = () => {
    setFormUserId('');
    setFormAmount('');
    setFormType('card_failed');
    setFormDescription('');
    setFormBountyId('');
  };

  const handleCreate = async () => {
    if (!formUserId || !formAmount || !formDescription.trim()) return;
    setSubmitting(true);

    const selectedUser = users.find((u) => u.id === formUserId);
    try {
      await createPaymentIssue({
        userId: formUserId,
        username: selectedUser?.anonymizedUsername || 'Unknown',
        userEmail: selectedUser?.email || '',
        bountyId: formBountyId || null,
        bountyTitle: null,
        amount: Math.round(parseFloat(formAmount) * 100),
        type: formType,
        description: formDescription.trim(),
        notes: '',
        createdByUid: currentUser.uid,
      });
      toast({ title: 'Payment issue created', status: 'success', duration: 3000, isClosable: true });
      resetForm();
      createModal.onClose();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateFee = async () => {
    const val = parseFloat(newFeePercent);
    if (isNaN(val) || val < 0 || val > 50) return;
    setUpdatingFee(true);
    try {
      await callUpdateFeePercent(val);
      toast({ title: `Fee updated to ${val}%`, status: 'success', duration: 3000, isClosable: true });
      feeModal.onClose();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setUpdatingFee(false);
    }
  };

  const handleResolvePayment = async (issueId) => {
    setResolving(issueId);
    try {
      await callAdminResolvePayment(issueId);
      toast({ title: 'Payment resolved', status: 'success', duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setResolving(null);
    }
  };

  if (txLoading) return <LoadingSpinner message="Loading payments..." />;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between" align="center">
        <Box>
          <Heading size="lg">Incoming Payments</Heading>
          <Text color="gray.500" mt={1}>{transactions.length} total transactions</Text>
        </Box>
        <HStack spacing={3}>
          <Select maxW="200px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="manually_verified">Manually Verified</option>
          </Select>
          <Button
            leftIcon={<Icon as={FiPlus} />}
            colorScheme="orange"
            size="sm"
            onClick={createModal.onOpen}
          >
            Add Issue
          </Button>
        </HStack>
      </HStack>

      {/* Fee Configuration */}
      <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} p={5}>
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontWeight="semibold" fontSize="sm">Platform Fee</Text>
            <Text fontSize="2xl" fontWeight="bold" color="orange.500">{feeLoading ? '...' : `${feePercent}%`}</Text>
            <Text fontSize="xs" color="gray.500">Applied to all task payments on submission approval</Text>
          </Box>
          <Button
            leftIcon={<Icon as={FiSettings} />}
            size="sm"
            variant="outline"
            onClick={() => { setNewFeePercent(String(feePercent)); feeModal.onOpen(); }}
          >
            Change Fee
          </Button>
        </HStack>
      </Box>

      {filtered.length === 0 ? (
        <EmptyState
          title="No payments found"
          description={statusFilter === 'all' ? 'Incoming payments will appear here when submissions are approved.' : `No ${statusFilter} payments.`}
          icon={FiDollarSign}
        />
      ) : (
        <Box bg={bg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Task</Th>
                  <Th>Collector</Th>
                  <Th>Researcher</Th>
                  <Th isNumeric>Amount</Th>
                  <Th isNumeric>Fee</Th>
                  <Th isNumeric>Total</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((tx) => {
                  const issue = issuesByClaimId[tx.claimId];
                  const isFailed = tx.paymentStatus === 'failed';
                  const canResolve = isFailed && issue && issue.status === 'open';

                  return (
                    <Tr key={tx.id}>
                      <Td>
                        <Text fontSize="xs" color="gray.500">{formatRelativeTime(tx.createdAt)}</Text>
                      </Td>
                      <Td maxW="180px">
                        <Text
                          as={Link}
                          to={`/submissions/${tx.claimId}`}
                          fontSize="sm"
                          fontWeight="medium"
                          noOfLines={1}
                          color="orange.500"
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {tx.bountyTitle || tx.bountyId}
                        </Text>
                      </Td>
                      <Td>
                        <Text
                          as={Link}
                          to={`/admin/users/${tx.fromUid}`}
                          fontSize="sm"
                          color="blue.500"
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {tx.collectorUsername || tx.fromUid?.slice(0, 8)}
                        </Text>
                      </Td>
                      <Td>
                        <Text
                          as={Link}
                          to={`/admin/users/${tx.toUid}`}
                          fontSize="sm"
                          color="blue.500"
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {tx.claimantUsername || tx.toUid?.slice(0, 8)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm"><Price cents={tx.amount} size="sm" /></Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color="gray.500"><Price cents={tx.feeAmount} size="sm" /></Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" fontWeight="medium"><Price cents={tx.totalCharged} size="sm" /></Text>
                      </Td>
                      <Td>
                        <StatusBadge status={tx.paymentStatus} />
                      </Td>
                      <Td>
                        {isFailed && issue && (
                          <VStack spacing={1} align="stretch">
                            <Tooltip label={issue.error} hasArrow>
                              <Text fontSize="xs" color="red.500" noOfLines={1} cursor="help">
                                {issue.stripeErrorCode || 'Error'}
                              </Text>
                            </Tooltip>
                            {canResolve && (
                              <Button
                                size="xs"
                                colorScheme="green"
                                onClick={() => handleResolvePayment(issue.id)}
                                isLoading={resolving === issue.id}
                                loadingText="..."
                              >
                                Mark Resolved
                              </Button>
                            )}
                            {issue.status === 'resolved' && (
                              <Text fontSize="xs" color="green.500">Resolved</Text>
                            )}
                          </VStack>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Create Issue Modal */}
      <Modal isOpen={createModal.isOpen} onClose={() => { createModal.onClose(); resetForm(); }} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Payment Issue</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">User</FormLabel>
                <Select
                  placeholder="Select user..."
                  value={formUserId}
                  onChange={(e) => setFormUserId(e.target.value)}
                >
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.anonymizedUsername} ({u.email})
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Amount (USD)</FormLabel>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="50.00"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Type</FormLabel>
                <Select value={formType} onChange={(e) => setFormType(e.target.value)}>
                  <option value="card_failed">Card Failed</option>
                  <option value="wire_pending">Wire Pending</option>
                  <option value="refund">Refund</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Task ID (optional)</FormLabel>
                <Input
                  placeholder="Task document ID"
                  value={formBountyId}
                  onChange={(e) => setFormBountyId(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Textarea
                  placeholder="Describe the payment issue..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => { createModal.onClose(); resetForm(); }}>Cancel</Button>
            <Button
              colorScheme="orange"
              onClick={handleCreate}
              isLoading={submitting}
              isDisabled={!formUserId || !formAmount || !formDescription.trim()}
            >
              Create Issue
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Fee Config Modal */}
      <Modal isOpen={feeModal.isOpen} onClose={feeModal.onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Platform Fee</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Current fee: <strong>{feePercent}%</strong>. This fee is added on top of the task reward when a submission is approved.
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">New Fee Percentage</FormLabel>
                <NumberInput
                  min={0}
                  max={50}
                  value={newFeePercent}
                  onChange={(val) => setNewFeePercent(val)}
                >
                  <NumberInputField placeholder="10" />
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={feeModal.onClose}>Cancel</Button>
            <Button
              colorScheme="orange"
              onClick={handleUpdateFee}
              isLoading={updatingFee}
              isDisabled={!newFeePercent || parseFloat(newFeePercent) < 0 || parseFloat(newFeePercent) > 50}
            >
              Update Fee
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
